import styled from '@emotion/styled';
import { ArrowRight } from 'lucide-react';
import type { CustomerSubscription } from '@/domain/entities/subscription.entity';

const DESKTOP_BREAKPOINT = '768px';

const Backdrop = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: flex-end;
  justify-content: center;
  z-index: 1000;

  @media (min-width: ${DESKTOP_BREAKPOINT}) {
    align-items: center;
  }
`;

const Dialog = styled.div`
  background: #fff;
  width: 100%;
  max-width: 480px;
  border-radius: 16px 16px 0 0;
  padding: 32px 24px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 24px;
  max-height: 90vh;
  overflow-y: auto;

  @media (min-width: ${DESKTOP_BREAKPOINT}) {
    border-radius: 16px;
    max-width: 560px;
    padding: 40px 32px;
  }
`;

const Title = styled.h2`
  font-size: 20px;
  font-weight: 700;
  line-height: 1.4;
  color: #292929;
  margin: 0;
  text-align: center;

  @media (min-width: ${DESKTOP_BREAKPOINT}) {
    font-size: 24px;
  }
`;

const ComparisonRow = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  width: 100%;
  justify-content: center;

  @media (min-width: ${DESKTOP_BREAKPOINT}) {
    gap: 24px;
  }
`;

const ProductColumn = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  flex: 1;
  min-width: 0;
`;

const Badge = styled.span<{ variant: 'current' | 'new' }>`
  display: inline-block;
  padding: 4px 12px;
  border-radius: 4px;
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: #fff;
  background: ${({ variant }) =>
    variant === 'current' ? '#292929' : '#0d9488'};
`;

const ProductThumb = styled.div<{ bgUrl?: string }>`
  width: 100%;
  max-width: 140px;
  aspect-ratio: 3 / 4;
  border-radius: 8px;
  background: ${({ bgUrl }) =>
    bgUrl ? `url(${bgUrl}) center/cover no-repeat` : '#f0f0f0'};

  @media (min-width: ${DESKTOP_BREAKPOINT}) {
    max-width: 160px;
  }
`;

const ProductLabel = styled.span`
  font-size: 13px;
  font-weight: 500;
  color: #292929;
  text-align: center;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

const ArrowContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  color: #292929;
`;

const Subtitle = styled.p`
  font-size: 14px;
  font-weight: 400;
  line-height: 1.43;
  color: #5a5a5a;
  margin: 0;
  text-align: center;
`;

const ConfirmButton = styled.button`
  width: 100%;
  padding: 14px 0;
  border: none;
  border-radius: 8px;
  background: #292929;
  color: #fff;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.15s;

  &:hover:not(:disabled) {
    background: #444;
  }

  &:disabled {
    background: #ccc;
    cursor: not-allowed;
  }
`;

const CancelButton = styled.button`
  background: none;
  border: none;
  padding: 8px 0;
  font-size: 14px;
  font-weight: 500;
  color: #5a5a5a;
  text-decoration: underline;
  cursor: pointer;

  &:hover {
    color: #292929;
  }
`;

const ErrorBanner = styled.div`
  width: 100%;
  padding: 12px 16px;
  border-radius: 8px;
  background: #fef2f2;
  border: 1px solid #fecaca;
  color: #991b1b;
  font-size: 14px;
  line-height: 1.43;
  text-align: center;
`;

function formatDate(dateStr: string | null): string {
  if (!dateStr) return '';
  const date = new Date(dateStr);
  const mm = String(date.getUTCMonth() + 1).padStart(2, '0');
  const dd = String(date.getUTCDate()).padStart(2, '0');
  const yy = String(date.getUTCFullYear()).slice(-2);
  return `${mm}/${dd}/${yy}`;
}

interface SwapConfirmationDialogProps {
  readonly subscription: CustomerSubscription;
  readonly newProduct: {
    id: string;
    title: string;
    imageUrl?: string;
  };
  readonly isLoading: boolean;
  readonly error?: string | null;
  readonly onConfirm: () => void;
  readonly onCancel: () => void;
}

export function SwapConfirmationDialog({
  subscription,
  newProduct,
  isLoading,
  error,
  onConfirm,
  onCancel,
}: SwapConfirmationDialogProps) {
  const currentItem = subscription.items?.[0] ?? null;

  return (
    <Backdrop onClick={onCancel} data-testid="swap-confirm-backdrop">
      <Dialog onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true" aria-labelledby="swap-confirm-title">
        <Title id="swap-confirm-title">Are you sure you want to swap?</Title>

        <ComparisonRow>
          <ProductColumn>
            <Badge variant="current">Current subscription</Badge>
            <ProductThumb
              bgUrl={currentItem?.image_url}
              role="img"
              aria-label={currentItem?.name ?? 'No current product'}
            />
            <ProductLabel>{currentItem?.name ?? 'No product'}</ProductLabel>
          </ProductColumn>

          <ArrowContainer>
            <ArrowRight size={24} />
          </ArrowContainer>

          <ProductColumn>
            <Badge variant="new">You&apos;re swapping to</Badge>
            <ProductThumb
              bgUrl={newProduct.imageUrl}
              role="img"
              aria-label={newProduct.title}
            />
            <ProductLabel>{newProduct.title}</ProductLabel>
          </ProductColumn>
        </ComparisonRow>

        {subscription.next_charge_date && (
          <Subtitle>
            Swapping will change your order of {formatDate(subscription.next_charge_date)}
          </Subtitle>
        )}

        {error && <ErrorBanner role="alert">{error}</ErrorBanner>}

        <ConfirmButton
          type="button"
          onClick={onConfirm}
          disabled={isLoading}
          aria-label="Confirm swap"
        >
          {isLoading ? 'Swapping...' : 'Yes, swap items'}
        </ConfirmButton>

        <CancelButton type="button" onClick={onCancel}>
          Cancel
        </CancelButton>
      </Dialog>
    </Backdrop>
  );
}
